import React, { useState } from 'react';
import { Plus, Trash2, Eye, EyeOff, Edit2, Image, X, Save, RefreshCw } from 'lucide-react';
import { T, CARD, CATEGORIES, INPUT_CLS, INPUT_STYLE, Field, DarkInput, GoldBtn } from './ContentShared';

const GalleryPanel = ({ images, onAdd, onUpdate, onDelete, saving }) => {
  const [filterCategory, setFilterCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [newImage, setNewImage] = useState({ url: '', title: '', category: 'other', description: '' });

  const filteredImages = filterCategory ? images.filter(img => img.category === filterCategory) : images;

  const handleAdd = async () => {
    await onAdd(newImage);
    setShowAddModal(false);
    setNewImage({ url: '', title: '', category: 'other', description: '' });
  };

  const handleUpdate = async (imageId, updates) => {
    await onUpdate(imageId, updates);
    setEditingImage(null);
  };

  return (
    <div data-testid="gallery-panel">
      <div className="flex items-center justify-between mb-6">
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-4 py-2.5 rounded-xl text-sm" style={INPUT_STYLE}>
          <option value="">Toutes ({images.length})</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <GoldBtn onClick={() => setShowAddModal(true)} testId="add-gallery-btn"><Plus className="w-4 h-4" /> Ajouter</GoldBtn>
      </div>

      {filteredImages.length === 0 ? (
        <div className="text-center py-16 rounded-2xl" style={CARD}>
          <Image className="w-12 h-12 mx-auto mb-4" style={{ color: T.border }} />
          <p style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>Aucune image</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredImages.map(img => (
            <div key={img.image_id} className={`relative group rounded-2xl overflow-hidden bg-white ${!img.visible ? 'opacity-40' : ''}`} style={CARD}>
              <div className="aspect-[4/3] relative overflow-hidden" style={{ background: '#f5f3ef' }}>
                <img src={img.url} alt={img.title} className="w-full h-full object-cover" onError={e => { e.target.src = 'https://via.placeholder.com/400x300?text=Image'; }} />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => handleUpdate(img.image_id, { visible: !img.visible })} className="p-2 rounded-lg bg-white/90">
                    {img.visible ? <EyeOff className="w-4 h-4" style={{ color: T.textPrimary }} /> : <Eye className="w-4 h-4" style={{ color: T.textPrimary }} />}
                  </button>
                  <button onClick={() => setEditingImage(img)} className="p-2 rounded-lg bg-white/90"><Edit2 className="w-4 h-4" style={{ color: T.textPrimary }} /></button>
                  <button onClick={() => onDelete(img.image_id)} className="p-2 rounded-lg bg-white/90"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </div>
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-1 rounded-full text-[10px] font-medium" style={{ background: 'rgba(255,255,255,0.85)', color: T.textSecondary }}>
                    {CATEGORIES.find(c => c.value === img.category)?.label || img.category}
                  </span>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-medium truncate text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{img.title}</h3>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {(showAddModal || editingImage) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl overflow-hidden bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div className="flex items-center justify-between p-5" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h2 className="text-base font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>{editingImage ? 'Modifier' : 'Ajouter'}</h2>
              <button onClick={() => { setShowAddModal(false); setEditingImage(null); }} style={{ color: T.textMuted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="URL *"><DarkInput value={editingImage?.url || newImage.url} onChange={e => editingImage ? setEditingImage({ ...editingImage, url: e.target.value }) : setNewImage({ ...newImage, url: e.target.value })} placeholder="https://..." /></Field>
              {(editingImage?.url || newImage.url) && <img src={editingImage?.url || newImage.url} alt="" className="h-32 w-full object-cover rounded-xl opacity-70" />}
              <Field label="Titre *"><DarkInput value={editingImage?.title || newImage.title} onChange={e => editingImage ? setEditingImage({ ...editingImage, title: e.target.value }) : setNewImage({ ...newImage, title: e.target.value })} /></Field>
              <Field label="Catégorie">
                <select value={editingImage?.category || newImage.category} onChange={e => editingImage ? setEditingImage({ ...editingImage, category: e.target.value }) : setNewImage({ ...newImage, category: e.target.value })}
                  className={INPUT_CLS} style={INPUT_STYLE}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Description"><DarkInput value={editingImage?.description || newImage.description} onChange={e => editingImage ? setEditingImage({ ...editingImage, description: e.target.value }) : setNewImage({ ...newImage, description: e.target.value })} rows={2} /></Field>
              {editingImage && <Field label="Ordre"><DarkInput type="number" value={editingImage.order} onChange={e => setEditingImage({ ...editingImage, order: parseInt(e.target.value) || 0 })} /></Field>}
            </div>
            <div className="flex justify-end gap-3 p-5" style={{ borderTop: `1px solid ${T.border}` }}>
              <button onClick={() => { setShowAddModal(false); setEditingImage(null); }} className="px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#f5f3ef', color: T.textSecondary, fontFamily: "'Montserrat', sans-serif" }}>Annuler</button>
              <GoldBtn disabled={saving} onClick={() => editingImage ? handleUpdate(editingImage.image_id, { url: editingImage.url, title: editingImage.title, category: editingImage.category, description: editingImage.description, order: editingImage.order }) : handleAdd()}>
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editingImage ? 'Enregistrer' : 'Ajouter'}
              </GoldBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPanel;
